import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { LINE_ITEM_KIND_LABELS, type LineItem, type LineItemKind } from '@/types/sales';
import { formatCurrency } from '@/lib/utils';

type Props = {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
  showCost?: boolean;
};

export function LineItemEditor({ items, onChange, showCost }: Props) {
  function update(idx: number, patch: Partial<LineItem>) {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }

  function remove(idx: number) {
    onChange(items.filter((_, i) => i !== idx));
  }

  function add() {
    onChange([
      ...items,
      {
        id: crypto.randomUUID(),
        kind: 'panel',
        description: '',
        quantity: 1,
        unitPrice: 0,
        unit: 'ea',
        taxable: true,
      },
    ]);
  }

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-6 text-center">
          <p className="text-sm text-text-muted mb-3">No line items yet.</p>
          <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={add}>
            Add First Item
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {items.map((it, idx) => (
              <LineItemRow
                key={it.id}
                item={it}
                index={idx}
                onChange={(patch) => update(idx, patch)}
                onRemove={() => remove(idx)}
                showCost={showCost}
              />
            ))}
          </div>
          <Button size="sm" variant="secondary" leftIcon={<Plus size={13} />} onClick={add}>
            Add Line Item
          </Button>
        </>
      )}
    </div>
  );
}

function LineItemRow({
  item, index, onChange, onRemove, showCost,
}: {
  item: LineItem;
  index: number;
  onChange: (patch: Partial<LineItem>) => void;
  onRemove: () => void;
  showCost?: boolean;
}) {
  const lineTotal = item.quantity * item.unitPrice;
  const lineCost = item.unitCost !== undefined ? item.quantity * item.unitCost : undefined;

  return (
    <div className="bg-bg-subtle border border-border rounded-sm p-4">
      <div className="flex items-start gap-3">
        <div className="shrink-0 flex items-center gap-2 pt-2 text-text-dim">
          <GripVertical size={14} />
          <span className="kicker w-6 text-center tabular">{String(index + 1).padStart(2, '0')}</span>
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          {/* Row 1: kind + description */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <Select
                value={item.kind}
                onChange={(e) => onChange({ kind: e.target.value as LineItemKind })}
                options={Object.entries(LINE_ITEM_KIND_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              />
            </div>
            <div className="col-span-9">
              <Input
                placeholder="Description (e.g. Walnut quartered, slip-matched)"
                value={item.description}
                onChange={(e) => onChange({ description: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Row 2: SKU, veneer, finish, dimensions */}
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-3">
              <Input
                placeholder="SKU"
                value={item.sku ?? ''}
                onChange={(e) => onChange({ sku: e.target.value })}
              />
            </div>
            <div className="col-span-3">
              <Input
                placeholder="Veneer species"
                value={item.veneerSpecies ?? ''}
                onChange={(e) => onChange({ veneerSpecies: e.target.value })}
              />
            </div>
            <div className="col-span-3">
              <Input
                placeholder="Finish"
                value={item.finish ?? ''}
                onChange={(e) => onChange({ finish: e.target.value })}
              />
            </div>
            <div className="col-span-3">
              <Input
                placeholder="Dimensions"
                value={item.dimensions ?? ''}
                onChange={(e) => onChange({ dimensions: e.target.value })}
              />
            </div>
          </div>

          {/* Row 3: qty, unit, price, [cost], line total */}
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-2">
              <Input
                label="Qty"
                type="number"
                min="0"
                step="any"
                value={item.quantity}
                onChange={(e) => onChange({ quantity: Number(e.target.value) || 0 })}
              />
            </div>
            <div className="col-span-2">
              <Select
                label="Unit"
                value={item.unit ?? 'ea'}
                onChange={(e) => onChange({ unit: e.target.value })}
                options={[
                  { value: 'ea', label: 'ea' },
                  { value: 'sf', label: 'sf' },
                  { value: 'lf', label: 'lf' },
                  { value: 'hr', label: 'hr' },
                ]}
              />
            </div>
            <div className="col-span-3">
              <Input
                label="Unit Price"
                type="number"
                min="0"
                step="0.01"
                value={item.unitPrice}
                onChange={(e) => onChange({ unitPrice: Number(e.target.value) || 0 })}
              />
            </div>
            {showCost && (
              <div className="col-span-3">
                <Input
                  label="Unit Cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unitCost ?? ''}
                  onChange={(e) => onChange({
                    unitCost: e.target.value === '' ? undefined : Number(e.target.value),
                  })}
                  hint={lineCost !== undefined ? `Line cost: ${formatCurrency(lineCost)}` : 'Optional'}
                />
              </div>
            )}
            <div className={showCost ? 'col-span-2' : 'col-span-5'}>
              <div className="kicker mb-2">Line Total</div>
              <div className="h-11 px-3.5 flex items-center justify-end font-display text-lg text-brass-300 tabular bg-bg-panel border border-border rounded-sm">
                {formatCurrency(lineTotal)}
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={onRemove}
          className="shrink-0 p-2 text-text-dim hover:text-danger hover:bg-danger/10 rounded-sm transition-colors"
          aria-label="Remove line item"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}
